/**
 * 마스터 토글 패널 — 매장 조건 설정
 * 권위 문서: docs/console/2026-05-14_corporate-store-opening-procedure.md §6.2
 *
 * shadcn 미설치 → 직접 구현 (Switch, RadioGroup, Tooltip)
 * 스타일 토큰: bg-surface, border-bdr-subtlest, text-fg-subtler 등 기존 패턴 그대로
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import type { ToggleState, BusinessType, FoodServiceType, CorporateTaxMode } from './toggleConfig';
import {
  BOOL_TOGGLES,
  CATEGORY_LABELS,
  CORPORATE_TAX_OPTIONS,
} from './toggleConfig';

/** 체크박스 컴포넌트 */
function Checkbox({
  id,
  checked,
  onToggle,
  disabled,
}: {
  id: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      role="checkbox"
      aria-checked={checked}
      id={id}
      onClick={disabled ? undefined : onToggle}
      className={[
        'w-4 h-4 rounded border-2 flex items-center justify-center shrink-0',
        'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        checked ? 'bg-fg border-fg' : 'bg-surface border-bdr-subtle hover:border-fg-subtler',
      ].join(' ')}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" className="text-surface" aria-hidden="true">
          <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

interface Props {
  state: ToggleState;
  onChange: (patch: Partial<ToggleState>) => void;
}

/** 토글 스위치 컴포넌트 */
function Switch({
  checked,
  onToggle,
  id,
  disabled,
}: {
  checked: boolean;
  onToggle: () => void;
  id: string;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      id={id}
      onClick={disabled ? undefined : onToggle}
      className={[
        'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent',
        'transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        checked ? 'bg-fg' : 'bg-bdr-subtle',
      ].join(' ')}
    >
      <span
        className={[
          'pointer-events-none inline-block h-4 w-4 rounded-full bg-surface shadow-sm',
          'transform transition-transform duration-200',
          checked ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
}

/** 툴팁 컴포넌트 */
function Tooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="text-fg-subtler hover:text-fg-subtle transition-colors"
        aria-label="도움말"
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <span
          className="absolute left-5 top-0 z-50 w-56 rounded-lg border border-bdr-subtlest bg-surface p-2.5 text-[11px] text-fg-subtle shadow-md leading-relaxed whitespace-normal"
          role="tooltip"
        >
          {text}
        </span>
      )}
    </span>
  );
}

/** 라디오 그룹 */
function RadioGroup<T extends string>({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer',
            'transition-colors duration-150',
            value === opt.value
              ? 'border-fg bg-fg text-surface font-medium'
              : 'border-bdr-subtle bg-surface text-fg-subtle hover:border-fg-subtler',
          ].join(' ')}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="sr-only"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

const BUSINESS_TYPE_OPTIONS: { value: BusinessType; label: string }[] = [
  { value: 'franchise', label: '프랜차이즈 가맹점' },
  { value: 'corporate', label: '법인 직영점' },
  { value: 'individual', label: '개인 사업자' },
];

const FOOD_SERVICE_OPTIONS: { value: FoodServiceType; label: string }[] = [
  { value: 'general', label: '일반음식점' },
  { value: 'rest', label: '휴게음식점' },
  { value: 'bakery', label: '제과점' },
];

export function ToggleControlsPanel({ state, onChange }: Props) {
  const [open, setOpen] = useState(true);

  // 카테고리별 토글 그룹핑
  const spaceToggles = BOOL_TOGGLES.filter((t) => t.category === 'space');
  const propertyToggles = BOOL_TOGGLES.filter((t) => t.category === 'property');
  const operationsToggles = BOOL_TOGGLES.filter((t) => t.category === 'operations');
  const staffingToggles = BOOL_TOGGLES.filter((t) => t.category === 'staffing');

  const isCorporate = state.businessType === 'corporate';

  return (
    <div className="rounded-xl border border-bdr-subtlest bg-surface overflow-hidden">
      {/* 패널 헤더 (접힘/펼침) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-alt transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">매장 조건 설정</span>
          <span className="text-[11px] text-fg-subtler">
            — 토글 선택 시 해당 절차가 자동 표시됩니다
          </span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-fg-subtler" />
        ) : (
          <ChevronDown className="w-4 h-4 text-fg-subtler" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-bdr-subtlest pt-4">

          {/* 사업자 형태 */}
          <ToggleSection label={CATEGORY_LABELS['business-type']}>
            <RadioGroup
              name="businessType"
              options={BUSINESS_TYPE_OPTIONS}
              value={state.businessType}
              onChange={(v) => onChange({ businessType: v })}
            />
          </ToggleSection>

          {/* 음식점 종별 */}
          <ToggleSection label={CATEGORY_LABELS['food-service-type']}>
            <RadioGroup
              name="foodServiceType"
              options={FOOD_SERVICE_OPTIONS}
              value={state.foodServiceType}
              onChange={(v) => onChange({ foodServiceType: v })}
            />
          </ToggleSection>

          {/* 공간 조건 */}
          <ToggleSection label={CATEGORY_LABELS['space']}>
            <div className="space-y-2.5">
              {spaceToggles.map((meta) => (
                <ToggleRow
                  key={meta.key}
                  id={meta.key}
                  label={meta.label}
                  tooltip={meta.tooltip}
                  checked={state[meta.key] as boolean}
                  onToggle={() => onChange({ [meta.key]: !state[meta.key] })}
                />
              ))}
            </div>
          </ToggleSection>

          {/* 부동산 */}
          <ToggleSection label={CATEGORY_LABELS['property']}>
            <div className="space-y-2.5">
              {propertyToggles.map((meta) => (
                <ToggleRow
                  key={meta.key}
                  id={meta.key}
                  label={meta.label}
                  tooltip={meta.tooltip}
                  checked={state[meta.key] as boolean}
                  onToggle={() => onChange({ [meta.key]: !state[meta.key] })}
                />
              ))}
            </div>
          </ToggleSection>

          {/* 운영 */}
          <ToggleSection label={CATEGORY_LABELS['operations']}>
            <div className="space-y-2.5">
              {operationsToggles.map((meta) => {
                // 주류 판매는 일반음식점 모드일 때만 활성
                const disabled = meta.key === 'sellsAlcohol' && state.foodServiceType !== 'general';
                return (
                  <ToggleRow
                    key={meta.key}
                    id={meta.key}
                    label={meta.label}
                    tooltip={
                      disabled
                        ? '휴게음식점·제과점은 주류 판매 불가'
                        : meta.tooltip
                    }
                    checked={state[meta.key] as boolean}
                    onToggle={() => onChange({ [meta.key]: !state[meta.key] })}
                    disabled={disabled}
                  />
                );
              })}
            </div>
          </ToggleSection>

          {/* 인력 */}
          <ToggleSection label={CATEGORY_LABELS['staffing']}>
            <div className="space-y-2.5">
              {staffingToggles.map((meta) => (
                <ToggleRow
                  key={meta.key}
                  id={meta.key}
                  label={meta.label}
                  tooltip={meta.tooltip}
                  checked={state[meta.key] as boolean}
                  onToggle={() => onChange({ [meta.key]: !state[meta.key] })}
                />
              ))}
            </div>
          </ToggleSection>

          {/* 법인 직영 전용 */}
          <div
            className={[
              'rounded-lg border p-4 space-y-4 transition-opacity duration-300',
              isCorporate
                ? 'border-bdr-subtle opacity-100'
                : 'border-bdr-subtlest opacity-40 pointer-events-none',
            ].join(' ')}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-fg-subtle">
                {CATEGORY_LABELS['corporate-only']}
              </span>
              {!isCorporate && (
                <span className="text-[10px] text-fg-subtler">(법인 직영 선택 시 활성)</span>
              )}
            </div>

            {/* 과세 단위 — 라디오 (양자택일) */}
            <div>
              <p className="text-[11px] text-fg-subtler mb-2">사업자등록 방식 (양자택일)</p>
              <RadioGroup
                name="corporateTaxMode"
                options={CORPORATE_TAX_OPTIONS.map((o) => ({
                  value: o.value as CorporateTaxMode,
                  label: o.label,
                }))}
                value={state.corporateTaxMode}
                onChange={(v) => onChange({ corporateTaxMode: v })}
              />
              <p className="text-[11px] text-fg-subtler mt-2 leading-relaxed">
                {CORPORATE_TAX_OPTIONS.find((o) => o.value === state.corporateTaxMode)?.tooltip}
              </p>
            </div>

            {/* 지점 설치 등기 — 체크박스 (과세 단위와 독립, 결합 가능) */}
            <div>
              <p className="text-[11px] text-fg-subtler mb-2">지점 등기 (과세 방식과 무관, 결합 가능)</p>
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="toggle-branchRegistration"
                  checked={state.branchRegistration}
                  onToggle={() => onChange({ branchRegistration: !state.branchRegistration })}
                  disabled={!isCorporate}
                />
                <label
                  htmlFor="toggle-branchRegistration"
                  className={[
                    'text-xs leading-snug',
                    isCorporate ? 'text-fg-subtle cursor-pointer' : 'text-fg-subtler cursor-not-allowed',
                  ].join(' ')}
                >
                  상법상 지점 설치 등기 (2호점 이상 법인 직영 시 상법 §181 의무)
                  <span className="block text-[10px] text-fg-subtler mt-0.5">
                    등기소 — 본점 2주·지점 3주 이내 / 등록면허세 40,200원 (과밀억제권역 3배 중과)
                  </span>
                </label>
                <Tooltip text="사업자등록 방식(별도/사업자단위과세)과 무관하게 체크 가능. 법인 직영 2호점 이상 시 상법상 자동 의무 발생." />
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

/** 섹션 래퍼 */
function ToggleSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-fg-subtler mb-2 uppercase tracking-wider">
        {label}
      </p>
      {children}
    </div>
  );
}

/** 개별 토글 행 */
function ToggleRow({
  id,
  label,
  tooltip,
  checked,
  onToggle,
  disabled,
}: {
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5 min-w-0">
        <label
          htmlFor={`toggle-${id}`}
          className={[
            'text-xs leading-snug cursor-pointer',
            disabled ? 'text-fg-subtler' : 'text-fg-subtle',
          ].join(' ')}
        >
          {label}
        </label>
        <Tooltip text={tooltip} />
      </div>
      <Switch
        id={`toggle-${id}`}
        checked={checked}
        onToggle={onToggle}
        disabled={disabled}
      />
    </div>
  );
}
